import type { CurrentActor } from "./session-api";

let activeAuthority = "signed-out";
let activeActorId: string | null = null;
let activeSessionId: string | null = null;
let requestRevision = 0;
export type CatalogueActorAuthority = Pick<
  CurrentActor,
  "authorityEpoch" | "catalogueScope" | "permissions" | "roles" | "userId"
>;
// Demo-only simulated directory. Never retains real profiles or session credentials.
const observedActors = new Map<string, CatalogueActorAuthority>();
export function observedCatalogueActor(userId?: string | null): CatalogueActorAuthority | null {
  const actor = observedActors.get(userId ?? activeActorId ?? "");
  return actor ? structuredClone(actor) : null;
}

// This opaque, client-only marker carries the initiating boundary across awaited header prep.
// The API middleware removes it before fetch; it is never a server authorization credential.
export const requestAuthorityHeader = "x-cyra-client-authority";

export type RequestAuthority = {
  actorId: string | null;
  revision: number;
  sessionId: string | null;
};

export function authorityFingerprint(actor: CurrentActor | CatalogueActorAuthority): string {
  return JSON.stringify({
    epoch: actor.authorityEpoch,
    permissions: [...actor.permissions].sort(),
    roles: [...actor.roles].sort(),
    scope:
      actor.catalogueScope.kind === "PLATFORM_ALL"
        ? { kind: "PLATFORM_ALL" }
        : actor.catalogueScope.kind === "PARTNER_SET"
          ? { kind: "PARTNER_SET", partnerIds: [...actor.catalogueScope.partnerIds].sort() }
          : {
              kind: "PARTNER_EXPERIENCE_SET",
              grants: actor.catalogueScope.grants
                .map((grant) => ({
                  ...grant,
                  experienceIds: [...grant.experienceIds].sort(),
                }))
                .sort((a, b) =>
                  `${a.partnerId}:${a.destinationId}`.localeCompare(
                    `${b.partnerId}:${b.destinationId}`,
                  ),
                ),
            },
    userId: actor.userId,
  });
}

export function setAuthorityState(actor: CurrentActor | null, retainSyntheticActor = false): void {
  if (retainSyntheticActor && actor)
    observedActors.set(
      actor.userId,
      structuredClone({
        authorityEpoch: actor.authorityEpoch,
        catalogueScope: actor.catalogueScope,
        permissions: actor.permissions,
        roles: actor.roles,
        userId: actor.userId,
      }),
    );
  const authority = actor ? authorityFingerprint(actor) : "signed-out";
  const sessionId = actor?.sessionId ?? null;
  if (authority !== activeAuthority || sessionId !== activeSessionId) requestRevision += 1;
  activeAuthority = authority;
  activeActorId = actor?.userId ?? null;
  activeSessionId = sessionId;
}

export function captureRequestAuthority(): RequestAuthority {
  return { actorId: activeActorId, revision: requestRevision, sessionId: activeSessionId };
}

export function assertRequestAuthority(
  authority: RequestAuthority,
  sessionId?: string | null,
): void {
  if (
    authority.revision !== requestRevision ||
    (authority.sessionId !== null && sessionId !== undefined && authority.sessionId !== sessionId)
  ) {
    throw new DOMException("Authority changed before the request could be sent.", "AbortError");
  }
}

export function currentQueryAuthority(): string {
  return activeAuthority;
}

export function authorityQueryKey<const T extends readonly unknown[]>(root: string, ...parts: T) {
  return [root, { authority: activeAuthority }, ...parts] as const;
}
